/**
 * TanStack Query v5 hooks for the Owner Support Case Management API (032).
 *
 * Covers:
 *   - useSupportCaseList       — paginated list with optional filters
 *   - useSupportCaseDetail     — single case with actions[] history
 *   - useActiveSupportCaseCount — badge count (OPEN + IN_PROGRESS total)
 *   - useBookingTimeline       — full booking journey timeline
 *   - useOpenSupportCase       — mutation: open a new case
 *   - useSubmitSupportAction   — mutation: submit a support action
 *   - useResolveSupportCase    — mutation: resolve a case
 *   - useAbandonSupportCase    — mutation: abandon a case
 *
 * Invalidation rules per contract section 8:
 *   openCase     → SUPPORT_CASE_KEYS.all
 *   submitAction → SUPPORT_CASE_KEYS.detail(caseId) + [BOOKINGS_QUERY_KEY]
 *   resolveCase  → SUPPORT_CASE_KEYS.all + SUPPORT_CASE_KEYS.detail(caseId)
 *   abandonCase  → SUPPORT_CASE_KEYS.all + SUPPORT_CASE_KEYS.detail(caseId)
 *
 * Feature: 032-booking-recovery-linking
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supportCaseService } from '../services/supportCaseService';
import type {
  OpenSupportCaseRequest,
  SubmitSupportActionRequest,
} from '../types/supportCase';
import { BOOKINGS_QUERY_KEY } from './useBookings';

// ─── Query key factory ────────────────────────────────────────────────────────

export const SUPPORT_CASE_KEYS = {
  all: ['supportCases'] as const,
  list: (params?: {
    bookingId?: string;
    statuses?: readonly string[];
    page?: number;
    size?: number;
    sort?: string;
  }) => [...SUPPORT_CASE_KEYS.all, 'list', params] as const,
  detail: (caseId: string) =>
    [...SUPPORT_CASE_KEYS.all, 'detail', caseId] as const,
  count: () => [...SUPPORT_CASE_KEYS.all, 'count'] as const,
} as const;

// ─── useSupportCaseList ───────────────────────────────────────────────────────

/**
 * Fetches a paginated list of support cases with optional filters.
 * Always enabled — callers control filtering via params.
 */
export const useSupportCaseList = (params?: {
  bookingId?: string;
  statuses?: readonly string[];
  page?: number;
  size?: number;
  sort?: string;
}) =>
  useQuery({
    queryKey: SUPPORT_CASE_KEYS.list(params),
    queryFn: () => supportCaseService.listCases(params as Parameters<typeof supportCaseService.listCases>[0]),
  });

// ─── useSupportCaseDetail ─────────────────────────────────────────────────────

/**
 * Fetches a single support case with its full actions[] history.
 * Disabled when caseId is empty string or undefined.
 */
export const useSupportCaseDetail = (caseId: string | undefined) =>
  useQuery({
    queryKey: SUPPORT_CASE_KEYS.detail(caseId ?? ''),
    queryFn: () => supportCaseService.getCase(caseId!),
    enabled: !!caseId,
  });

// ─── useActiveSupportCaseCount ────────────────────────────────────────────────

/**
 * Returns the count of active (non-final) support cases — OPEN + IN_PROGRESS.
 * Derives count from totalElements of the filtered list response.
 */
export const useActiveSupportCaseCount = () => {
  const params = { statuses: ['OPEN', 'IN_PROGRESS'] as const };

  const query = useQuery({
    queryKey: SUPPORT_CASE_KEYS.count(),
    queryFn: () => supportCaseService.listCases(params),
  });

  return {
    count: query.data?.totalElements ?? 0,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,
  };
};

// ─── useBookingTimeline ───────────────────────────────────────────────────────

/**
 * Fetches the full booking journey timeline for a given booking.
 * Disabled when bookingId is empty string or undefined.
 */
export const useBookingTimeline = (bookingId: string | undefined) =>
  useQuery({
    queryKey: [...SUPPORT_CASE_KEYS.all, 'timeline', bookingId] as const,
    queryFn: () => supportCaseService.getBookingTimeline(bookingId!),
    enabled: !!bookingId,
  });

// ─── useOpenSupportCase ───────────────────────────────────────────────────────

/**
 * Opens a new support case for a booking.
 * On success: invalidates all supportCases queries (list + count).
 */
export const useOpenSupportCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: OpenSupportCaseRequest) =>
      supportCaseService.openCase(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_CASE_KEYS.all });
    },
  });
};

// ─── useSubmitSupportAction ───────────────────────────────────────────────────

/**
 * Submits a support action for a given case.
 * On success: invalidates the specific case detail + the full bookings cache.
 */
export const useSubmitSupportAction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      caseId,
      request,
    }: {
      caseId: string;
      request: SubmitSupportActionRequest;
    }) => supportCaseService.submitAction(caseId, request),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: SUPPORT_CASE_KEYS.detail(variables.caseId),
      });
      queryClient.invalidateQueries({ queryKey: [BOOKINGS_QUERY_KEY] });
    },
  });
};

// ─── useResolveSupportCase ────────────────────────────────────────────────────

/**
 * Resolves a support case (status → RESOLVED).
 * On success: invalidates all supportCases queries + the specific case detail.
 */
export const useResolveSupportCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId: string) => supportCaseService.resolveCase(caseId),
    onSuccess: (_data, caseId) => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_CASE_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: SUPPORT_CASE_KEYS.detail(caseId),
      });
    },
  });
};

// ─── useAbandonSupportCase ────────────────────────────────────────────────────

/**
 * Abandons a support case (status → ABANDONED).
 * On success: invalidates all supportCases queries + the specific case detail.
 */
export const useAbandonSupportCase = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (caseId: string) => supportCaseService.abandonCase(caseId),
    onSuccess: (_data, caseId) => {
      queryClient.invalidateQueries({ queryKey: SUPPORT_CASE_KEYS.all });
      queryClient.invalidateQueries({
        queryKey: SUPPORT_CASE_KEYS.detail(caseId),
      });
    },
  });
};
